/**
 * Device-name derivation helper.
 *
 * Pure UA-string parser shared between the extension's background SW
 * and the web app's pairing flow. Both clients call
 * `deriveDeviceName(navigator.userAgent)` at the same two moments:
 *
 *   1. `POST /pair/init` — the server records this on the device row
 *      so the phone's pairing prompt can show "Chrome on macOS"
 *      instead of an opaque deviceId.
 *   2. Post-pair self-rename — the client PATCHes `/v1/devices/<self>`
 *      with the same name and writes it to `ReflectoStorage.selfDeviceName`
 *      so the local Devices view and the phone's Devices tab agree
 *      from the very first connect.
 *
 * NO side effects, NO platform globals (no `navigator`, no `chrome.*`,
 * no `self`). The caller passes in the UA string so this file can live
 * in `@reflecto/client-core` and stay testable without DOM mocking.
 */

/**
 * Derive a human-readable device name from a User-Agent string.
 *
 * Output forms: `"Chrome on macOS"`, `"Firefox on Windows"`,
 * `"Safari on iOS"`, `"Edge on Linux"`, etc.
 *
 * Falls back to `"Browser"` when the UA is empty / unrecognised so the
 * caller never has to special-case an empty return value. The caller
 * may layer its own platform-specific fallback (e.g. the extension's
 * legacy `"Browser Extension"` constant) if a non-empty default with
 * stronger semantics is preferred.
 *
 * @param userAgent The `navigator.userAgent` string. May be empty.
 */
export function deriveDeviceName(userAgent: string): string {
  const { browser, os } = deriveDeviceParts(userAgent);
  return os ? `${browser} on ${os}` : browser;
}

/** The two brand-name components of a derived device name. Browser and
 * OS names are proper nouns and never localize; only the joining
 * template does (the `joinName` seam in `autoNameDevice`). */
export interface DeviceNameParts {
  /** "Chrome" / "Firefox" / … — `"Browser"` when unrecognised. */
  browser: string;
  /** "macOS" / "Windows" / … — `""` when unrecognised. */
  os: string;
}

/**
 * The UA-parsing half of `deriveDeviceName`, exposed so callers that
 * localize the joining template ("Chrome on macOS" → "Chrome auf
 * macOS" / "macOS の Chrome") can compose the parts themselves.
 */
export function deriveDeviceParts(userAgent: string): DeviceNameParts {
  let browser = "Browser";
  // Edge / Opera both ship Chrome's "Chrome/" token, so they MUST be
  // probed before Chrome itself or every Edge/Opera install reports
  // as Chrome.
  if (/Firefox\//.test(userAgent)) browser = "Firefox";
  else if (/Edg\//.test(userAgent)) browser = "Edge";
  else if (/OPR\//.test(userAgent)) browser = "Opera";
  else if (/Chrome\//.test(userAgent)) browser = "Chrome";
  else if (/Safari\//.test(userAgent)) browser = "Safari";

  let os = "";
  // Mobile OSes share substrings with desktop OSes and MUST be probed
  // first:
  //   - iOS UAs contain "Mac OS X" (e.g. "iPhone; CPU iPhone OS 17_0
  //     like Mac OS X"), so iOS must be tested before macOS.
  //   - Android UAs contain "Linux" before "Android" (e.g.
  //     "Mozilla/5.0 (Linux; Android 14; Pixel 8) …"), so Android
  //     must be tested before Linux.
  if (/Windows/.test(userAgent)) os = "Windows";
  else if (/iPhone|iPad/.test(userAgent)) os = "iOS";
  else if (/Android/.test(userAgent)) os = "Android";
  else if (/Macintosh|Mac OS X/.test(userAgent)) os = "macOS";
  else if (/Linux/.test(userAgent)) os = "Linux";

  return { browser, os };
}
